import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
// --- Types ---
interface ScannedProduct {
  title: string;
  editionLabel?: string;
  priceNumber: number;
  priceText: string;
  thumbnailUrl?: string;
  weight: number;
}
// --- Buyer Names ---
const NAMES = ['María de Bogotá', 'Carlos de CDMX', 'Ana de Lima', 'Luis de Buenos Aires', 'Sofía de Santiago', 'Diego de Medellín', 'Valentina de Quito', 'Andrés de Miami', 'Camila de Madrid', 'Javier de Caracas', 'Isabella de Monterrey', 'Sebastián de Cali', 'Daniela de San José', 'Mateo de Panamá', 'Lucía de Guadalajara', 'Gabriel de Santo Domingo', 'Paula de Barranquilla', 'Nicolás de Guayaquil', 'Mariana de Puebla', 'Alejandro de La Paz'];
// --- Default fallback products ---
const DEFAULT_PRODUCTS: ScannedProduct[] = [{
  title: 'Pack Despertar',
  editionLabel: 'Physical Book',
  priceNumber: 27,
  priceText: '$27',
  weight: 70
}, {
  title: 'Edición Digital',
  editionLabel: 'Digital Edition',
  priceNumber: 17,
  priceText: '$17',
  weight: 30
}];
// --- DOM Product Scanner ---
function scanPageProducts(): ScannedProduct[] {
  if (typeof document === 'undefined') return DEFAULT_PRODUCTS;
  const products: ScannedProduct[] = [];
  // Strategy 1: JSON-LD structured data
  try {
    const jsonLdScripts = document.querySelectorAll('script[type="application/ld+json"]');
    jsonLdScripts.forEach(script => {
      try {
        const data = JSON.parse(script.textContent || '');
        const items = Array.isArray(data) ? data : [data];
        items.forEach((item: any) => {
          if (item['@type'] === 'Product' || item['@type'] === 'Offer') {
            const offer = item.offers || item;
            const price = parseFloat(offer.price || offer.lowPrice || '0');
            if (price > 0) {
              products.push({
                title: item.name || 'Product',
                priceNumber: price,
                priceText: `$${price.toFixed(2)}`,
                thumbnailUrl: item.image?.[0] || item.image || undefined,
                weight: 50
              });
            }
          }
        });
      } catch {
        /* skip malformed JSON-LD */
      }
    });
  } catch {
    /* skip */
  }
  if (products.length > 0) return assignWeights(products);
  // Strategy 2: Scan DOM for price patterns near CTAs and product sections
  const priceRegex = /\$\s?(\d+(?:\.\d{1,2})?)/g;
  // Find all text nodes with prices, excluding struck-through ones
  const allElements = document.querySelectorAll('*');
  const priceElements: {
    el: Element;
    price: number;
    priceText: string;
  }[] = [];
  allElements.forEach(el => {
    // Skip struck-through prices (line-through, <s>, <del>, <strike>)
    if (isStruckThrough(el)) return;
    // Skip elements inside legal modals or footers
    if (el.closest('footer') || el.closest('[class*="modal"]')) return;
    const text = getDirectText(el);
    if (!text) return;
    const matches = [...text.matchAll(priceRegex)];
    matches.forEach(match => {
      const price = parseFloat(match[1]);
      if (price > 0 && price < 1000) {
        priceElements.push({
          el,
          price,
          priceText: match[0].trim()
        });
      }
    });
  });
  // Deduplicate by price value (keep first occurrence which is usually most prominent)
  const seenPrices = new Set<number>();
  const uniquePriceElements = priceElements.filter(({
    price
  }) => {
    if (seenPrices.has(price)) return false;
    seenPrices.add(price);
    return true;
  });
  // For each unique price, find the nearest product title and image
  uniquePriceElements.forEach(({
    el,
    price,
    priceText
  }) => {
    const title = findNearestTitle(el);
    const thumbnail = findNearestImage(el);
    if (title) {
      products.push({
        title,
        priceNumber: price,
        priceText: `$${price % 1 === 0 ? price.toString() : price.toFixed(2)}`,
        thumbnailUrl: thumbnail || undefined,
        weight: 50
      });
    }
  });
  if (products.length > 0) return assignWeights(products);
  // Strategy 3: Fallback to defaults
  return DEFAULT_PRODUCTS;
}
function isStruckThrough(el: Element): boolean {
  const tag = el.tagName.toLowerCase();
  if (tag === 's' || tag === 'del' || tag === 'strike') return true;
  const style = window.getComputedStyle(el);
  if (style.textDecorationLine?.includes('line-through')) return true;
  // Check parent chain for line-through
  let parent = el.parentElement;
  let depth = 0;
  while (parent && depth < 5) {
    const parentTag = parent.tagName.toLowerCase();
    if (parentTag === 's' || parentTag === 'del' || parentTag === 'strike') return true;
    const parentStyle = window.getComputedStyle(parent);
    if (parentStyle.textDecorationLine?.includes('line-through')) return true;
    // Check for Tailwind line-through class
    if (parent.classList.contains('line-through')) return true;
    parent = parent.parentElement;
    depth++;
  }
  if (el.classList.contains('line-through')) return true;
  return false;
}
function getDirectText(el: Element): string {
  // Get only direct text content (not from children) to avoid duplicates
  let text = '';
  el.childNodes.forEach(node => {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent || '';
    }
  });
  return text.trim();
}
function findNearestTitle(priceEl: Element): string | null {
  // Look for headings (h1-h6) or bold/strong text near the price element
  const searchArea = priceEl.closest('section, [class*="card"], [class*="bump"], [class*="product"], div') || priceEl.parentElement;
  if (!searchArea) return null;
  // Try headings first
  const headings = searchArea.querySelectorAll('h1, h2, h3, h4, h5, h6');
  for (const h of headings) {
    const text = (h.textContent || '').trim();
    // Skip headings that are just prices or very short
    if (text.length > 3 && !text.match(/^\$\s?\d/)) {
      // Clean up the title
      return text.replace(/\s+/g, ' ').substring(0, 60);
    }
  }
  // Try bold/strong text
  const bolds = searchArea.querySelectorAll('strong, b, [class*="font-bold"]');
  for (const b of bolds) {
    const text = (b.textContent || '').trim();
    if (text.length > 5 && text.length < 80 && !text.match(/^\$\s?\d/)) {
      return text.replace(/\s+/g, ' ').substring(0, 60);
    }
  }
  return null;
}
function findNearestImage(priceEl: Element): string | null {
  const searchArea = priceEl.closest('section, [class*="card"], [class*="bump"], [class*="product"], div') || priceEl.parentElement;
  if (!searchArea) return null;
  const img = searchArea.querySelector('img[src]');
  if (img) {
    const src = img.getAttribute('src');
    if (src && !src.includes('data:image/svg')) return src;
  }
  return null;
}
function assignWeights(products: ScannedProduct[]): ScannedProduct[] {
  if (products.length === 0) return products;
  // Sort by price (lowest first = most likely main product on a sales page)
  const sorted = [...products].sort((a, b) => a.priceNumber - b.priceNumber);
  // Main product (cheapest/first) gets highest weight
  const total = 100;
  const mainWeight = Math.round(total * 0.6);
  const remainingWeight = total - mainWeight;
  const otherWeight = products.length > 1 ? Math.round(remainingWeight / (products.length - 1)) : 0;
  return sorted.map((p, i) => ({
    ...p,
    weight: i === 0 ? mainWeight : otherWeight
  }));
}
// --- Helpers ---
function getRandomName(): string {
  return NAMES[Math.floor(Math.random() * NAMES.length)];
}
function pickWeightedProduct(products: ScannedProduct[]): ScannedProduct {
  const totalWeight = products.reduce((sum, p) => sum + p.weight, 0);
  let rand = Math.random() * totalWeight;
  for (const product of products) {
    rand -= product.weight;
    if (rand <= 0) return product;
  }
  return products[0];
}
function timeAgo(): string {
  const mins = Math.floor(Math.random() * 12) + 1;
  return `hace ${mins} min`;
}
// --- Component ---
export function SalesPopup() {
  const [visible, setVisible] = useState(false);
  const [products, setProducts] = useState<ScannedProduct[]>(DEFAULT_PRODUCTS);
  const [data, setData] = useState({
    name: getRandomName(),
    product: DEFAULT_PRODUCTS[0],
    time: timeAgo()
  });
  const productsRef = useRef<ScannedProduct[]>(DEFAULT_PRODUCTS);
  // Scan DOM on mount (after render)
  useEffect(() => {
    const timer = setTimeout(() => {
      const scanned = scanPageProducts();
      setProducts(scanned);
      productsRef.current = scanned;
    }, 1000); // Wait 1s for DOM to fully render
    return () => clearTimeout(timer);
  }, []);
  const cycle = useCallback(() => {
    const currentProducts = productsRef.current;
    setData({
      name: getRandomName(),
      product: pickWeightedProduct(currentProducts),
      time: timeAgo()
    });
    setVisible(true);
    const hideTimer = setTimeout(() => {
      setVisible(false);
    }, 3000);
    return hideTimer;
  }, []);
  useEffect(() => {
    const initialDelay = setTimeout(() => {
      cycle();
      const interval = setInterval(() => {
        cycle();
      }, 6000);
      return () => clearInterval(interval);
    }, 4000);
    return () => clearTimeout(initialDelay);
  }, [cycle]);
  const displayPrice = data.product.priceText.includes('USD') ? data.product.priceText : `${data.product.priceText} USD`;
  return <div className="block md:hidden fixed bottom-4 left-0 right-0 z-[60] px-3 pointer-events-none" data-id="element-293">
      <AnimatePresence data-id="element-294">
        {visible && <motion.div initial={{
        opacity: 0,
        y: 30,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 10,
        scale: 0.98
      }} transition={{
        type: 'spring',
        damping: 20,
        stiffness: 300,
        mass: 0.8
      }} className="mx-auto max-w-[360px] pointer-events-auto" data-id="element-295">
            <div className="flex items-center gap-3 px-3.5 py-3 rounded-2xl border border-white/[0.08]" style={{
          background: 'rgba(30, 30, 30, 0.85)',
          backdropFilter: 'blur(40px)',
          WebkitBackdropFilter: 'blur(40px)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.5), 0 1px 3px rgba(0,0,0,0.3), inset 0 0.5px 0 rgba(255,255,255,0.06)'
        }} data-id="element-296">
              {/* Product Image */}
              <div className="w-10 h-10 rounded-lg overflow-hidden shrink-0 bg-[#111]" data-id="element-297">
                {data.product.thumbnailUrl ? <img src={data.product.thumbnailUrl} alt="" className="w-full h-full object-cover" data-id="element-298" /> : <div className="w-full h-full flex items-center justify-center text-[#f5a623] text-lg font-bold" data-id="element-299">
                    ⚡
                  </div>}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0" data-id="element-300">
                <div className="flex items-center justify-between gap-2" data-id="element-301">
                  <p className="text-white/90 text-[13px] font-semibold truncate leading-tight" style={{
                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif'
              }} data-id="element-302">
                    Compra verificada ✓
                  </p>
                  <span className="text-white/40 text-[11px] shrink-0" style={{
                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif'
              }} data-id="element-303">
                    {data.time}
                  </span>
                </div>
                <p className="text-white/60 text-[12px] leading-snug mt-0.5 truncate" style={{
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif'
            }} data-id="element-304">
                  {data.name} compró{' '}
                  <span className="text-white/80 font-medium" data-id="element-305">
                    {data.product.title}
                  </span>
                </p>
                <p className="text-[#00e676] text-[12px] font-semibold mt-0.5" style={{
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif'
            }} data-id="element-306">
                  por {displayPrice}
                </p>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}